import React from 'react';
import { Instagram, Linkedin } from 'lucide-react';
import StartTodayButton from './StartTodayButton';

// X.com icon as SVG since lucide doesn't have it
const XIcon = ({ size = 20, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    className={className}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const socialLinks = [
  {
    name: 'Instagram',
    handle: '@motifunbound',
    url: 'https://instagram.com/motifunbound',
    icon: Instagram
  },
  {
    name: 'LinkedIn',
    handle: 'motifunbound',
    url: 'https://linkedin.com/company/motifunbound',
    icon: Linkedin
  },
  {
    name: 'X',
    handle: '@motifunbound',
    url: 'https://x.com/motifunbound',
    icon: XIcon
  }
];

export default function ConnectWithUs({ variant = 'default' }) {
  if (variant === 'header') {
    return (
      <div className="flex items-center gap-4">
        <span className="hidden text-xs font-semibold uppercase tracking-[0.28em] text-[#0F3D3F] lg:inline">
          Connect
        </span>
        <div className="flex items-center gap-2">
          {socialLinks.map((social) => (
            <a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#BB3E00]/10 text-[#BB3E00] transition-all hover:bg-[#BB3E00]/20 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#BB3E00] focus-visible:ring-offset-2"
              aria-label={`Follow us on ${social.name}`}
            >
              <social.icon size={18} />
            </a>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'blog') {
    return (
      <div className="rounded-3xl border border-[#38A3A5]/20 bg-white p-8 shadow-sm">
        <h3 className="text-2xl font-semibold text-[#000000]">Stay Connected</h3>
        <p className="mt-3 text-sm text-gray-600 md:text-base">
          Follow Motif Unbound for weekly go-to-market insights and market expansion strategies.
        </p>
        <div className="mt-6 flex gap-3 justify-center">
          {socialLinks.map((social) => (
            <a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FFD965] text-[#000000] transition-all hover:bg-[#E5B84A] hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD965] focus-visible:ring-offset-2"
              aria-label={`Follow us on ${social.name}`}
            >
              <social.icon size={20} />
            </a>
          ))}
        </div>
        <div className="mt-6 pt-6 border-t border-gray-200">
          <StartTodayButton />
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className="flex flex-col gap-4 rounded-2xl border border-white/30 bg-white/10 px-5 py-4 backdrop-blur md:flex-row md:items-center md:justify-between">
        <div className="max-w-xs">
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">Connect</span>
          <p className="mt-2 text-sm text-white/80">
            Market expansion insights and GTM playbooks in real time.
          </p>
        </div>
        <div className="flex gap-3">
          {socialLinks.map((social) => (
            <a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-[#BB3E00] transition-all hover:bg-white/80 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#BB3E00] focus-visible:ring-offset-2"
              aria-label={`Follow us on ${social.name}`}
            >
              <social.icon size={20} />
            </a>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'footer') {
    return (
      <div>
        <h4 className="mb-4 font-semibold text-white">Connect With Us</h4>
        <div className="flex gap-3 mb-4">
          {socialLinks.map((social) => (
            <a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FFD965] text-[#000000] transition-all hover:bg-[#E5B84A] hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD965]"
              aria-label={`Follow us on ${social.name}`}
            >
              <social.icon size={18} />
            </a>
          ))}
        </div>
        <p className="text-sm text-white/70 mb-4">
          Weekly market expansion insights and GTM strategies.
        </p>
      </div>
    );
  }

  // Default variant - simplified section
  return (
    <div className="rounded-3xl border border-[#38A3A5]/20 bg-white p-8 md:p-10 shadow-sm">
      <h3 className="text-2xl font-semibold text-[#000000] md:text-3xl">
        Stay Connected
      </h3>
      <p className="mt-3 text-sm text-gray-600 md:text-base">
        Follow Motif Unbound for market-entry wins, partner spotlights, and go-to-market frameworks.
      </p>
      <div className="mt-6 flex gap-4 justify-center md:justify-start">
        {socialLinks.map((social) => (
          <a
            key={social.name}
            href={social.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FFD965] text-[#000000] transition-all hover:bg-[#E5B84A] hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD965] focus-visible:ring-offset-2"
            aria-label={`Follow us on ${social.name}`}
          >
            <social.icon size={22} />
          </a>
        ))}
      </div>
      <div className="mt-8 pt-6 border-t border-gray-200">
        <StartTodayButton variant="large" />
      </div>
    </div>
  );
}
